import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal, MaskLines } from "@/components/Reveal";

const SORTS = [["featured", "Featured"], ["low", "Price ↑"], ["high", "Price ↓"]];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => setCats([]));
  }, []);

  useEffect(() => {
    setProducts(null);
    api.get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProducts([]));
  }, [category]);

  const shown = useMemo(() => {
    let list = (products || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    if (sort === "low") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "featured") list = [...list].sort((a, b) => Number(b.featured) - Number(a.featured));
    return list;
  }, [products, q, sort]);

  const pill = (active) => `rounded-full border px-4 py-2 text-sm capitalize transition-colors duration-300 ${active ? "border-ink bg-ink text-bone" : "border-line hover:border-ink"}`;

  return (
    <main data-testid="shop-page" className="mx-auto max-w-[1600px] px-5 pb-32 pt-28 md:px-10 md:pt-36">
      <p className="eyebrow">{shown.length} garments</p>
      <h1 className="mt-4 font-display text-6xl font-light capitalize leading-none md:text-8xl">
        <MaskLines key={category} lines={[category || "All garments"]} lineClass={category ? "italic" : ""} />
      </h1>
      <div className="sticky top-16 z-30 -mx-5 mt-10 flex flex-col gap-4 border-y border-line bg-bone/90 px-5 py-4 backdrop-blur-xl md:top-20 md:-mx-10 md:flex-row md:items-center md:justify-between md:px-10">
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="category-filter">
          <button data-testid="filter-all" className={pill(!category)} onClick={() => setParams({})}>All</button>
          {cats.map((c) => (
            <button key={c} data-testid={`filter-${c}`} className={pill(category === c)} onClick={() => setParams({ category: c })}>{c}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex flex-1 items-center gap-2 border-b border-ink/40 pb-1 md:w-56 md:flex-none">
            <Search className="h-4 w-4 text-stone" />
            <input data-testid="shop-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="w-full bg-transparent text-sm outline-none placeholder:text-stone" />
          </label>
          <div className="flex gap-1">
            {SORTS.map(([k, l]) => (
              <button key={k} data-testid={`sort-${k}`} onClick={() => setSort(k)} className={`px-2 py-1 font-mono text-xs ${sort === k ? "text-ink underline underline-offset-4" : "text-stone"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {products === null ? (
        <div className="mt-12 grid grid-cols-2 gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="aspect-[3/4] animate-pulse bg-sand" />)}
        </div>
      ) : shown.length === 0 ? (
        <p data-testid="shop-empty" className="mt-20 font-display text-4xl italic text-stone">Nothing matches — try another room.</p>
      ) : (
        <div data-testid="product-grid" className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 md:gap-x-6 md:gap-y-16">
          {shown.map((p, i) => (
            <Reveal key={p.product_id} delay={(i % 3) * 0.08} className={i % 3 === 1 ? "md:mt-16" : ""}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      )}
    </main>
  );
}
